import { useEffect, useMemo, useState } from "react";
import { PopupCampaignsAdminService } from "../api/generated/services/PopupCampaignsAdminService";
import { PopupCampaignPostalCodeViewModel } from "../api/generated/models/PopupCampaignPostalCodeViewModel";
import { deriveLocationOptions, LocationOptions } from "../helpers/popupCampaignTargeting";
import { apiErrorMessage } from "../helpers/popupCampaignForm";

/**
 * The ~1,089-row Danish postal-code reference table. Loaded once per mount: it is a few tens of
 * kilobytes, it changes about once a decade, and every location control needs it.
 */
export const usePostalCodes = () => {
  const [postalCodes, setPostalCodes] = useState<PopupCampaignPostalCodeViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    PopupCampaignsAdminService.getApiPopupCampaignsAdminPostalCodes()
      .then((rows) => {
        if (!cancelled) setPostalCodes(rows ?? []);
      })
      .catch((e) => {
        console.error("Error loading postal codes:", e);
        if (!cancelled) setError(apiErrorMessage(e, "Could not load the postal-code list"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const options: LocationOptions = useMemo(() => deriveLocationOptions(postalCodes), [postalCodes]);

  // The raw rows aren't returned: LocationPicker (the only consumer) only ever needs the derived
  // option lists, loading state and error — keep the surface to what's actually used.
  return { options, loading, error };
};
